import React from 'react';
import { IconAlertTriangle, IconBook, IconRefresh, IconServer } from '@tabler/icons-react';

const ErrorLoadingExam = () => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-red-50 via-orange-50 to-red-50 p-6">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Error indicator bar */}
        <div className="h-1 bg-gray-100 w-full">
          <div className="h-full bg-gradient-to-r from-red-500 to-orange-500 w-full"></div>
        </div>
        
        <div className="p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Unable to Load Your Exam</h2>
          
          {/* Error icon animation container */}
          <div className="relative h-32 mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Shaking alert icon */}
              <div className="absolute h-20 w-20 bg-red-100 rounded-full flex items-center justify-center error-pulse">
                <IconAlertTriangle className="text-red-500 h-10 w-10 error-shake" />
              </div>
              
              {/* Orbiting icons showing disruption */}
              <div className="quiz-icon-orbit-disrupted">
                <IconBook className="text-red-400 h-8 w-8 icon-flicker" />
                <IconServer className="text-orange-500 h-8 w-8 error-shake" />
                <IconRefresh className="text-amber-500 h-8 w-8 icon-flicker" />
              </div>
            </div>
          </div>
          
          {/* Error message */}
          <div className="text-center mb-8">
            <p className="text-red-600 error-message">Connection error occurred</p>
            <p className="text-gray-600 mt-2">We encountered a problem while loading your exam</p>
          </div>
          
          {/* Broken paper stack */}
          <div className="relative h-20 mx-auto w-40">
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform rotate-12 top-6 left-6 paper-scattered-1"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform -rotate-8 top-2 -left-5 paper-scattered-2"></div>
            <div className="paper-stack p-4 bg-white border border-red-200 rounded shadow absolute w-full h-full transform rotate-1 top-0 left-0 z-10 border-dashed">
              <div className="h-2 w-12 bg-red-200 rounded mb-2"></div>
              <div className="h-2 w-24 bg-red-200 rounded mb-2"></div>
              <div className="h-2 w-16 bg-red-200 rounded"></div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="mt-10 flex flex-col space-y-3">
            <button className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center">
              <IconRefresh className="mr-2 h-5 w-5" />
              Try Again
            </button>
            <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg transition-colors duration-200">
              Contact Support
            </button>
          </div>
          
          {/* Helpful tip */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm fade-in-up">If the problem persists, please try again later or contact your administrator</p>
          </div>
        </div>
      </div>
      
      {/* CSS animations */}
      <style jsx="true">{`
        @keyframes error-pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
          }
          70% { 
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(220, 38, 38, 0);
          }
        }
        
        @keyframes error-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-3px); }
          40% { transform: translateX(3px); }
          60% { transform: translateX(-2px); }
          80% { transform: translateX(2px); }
        }
        
        @keyframes orbit-disrupted {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(90deg); }
          30% { transform: rotate(85deg); }
          45% { transform: rotate(170deg); }
          50% { transform: rotate(165deg); }
          75% { transform: rotate(270deg); }
          80% { transform: rotate(265deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes scatter-1 {
          0%, 100% { transform: rotate(12deg) translateX(0) translateY(0); }
          50% { transform: rotate(15deg) translateX(2px) translateY(-1px); }
        }
        
        @keyframes scatter-2 {
          0%, 100% { transform: rotate(-8deg) translateX(0) translateY(0); }
          50% { transform: rotate(-10deg) translateX(-2px) translateY(-2px); }
        }
        
        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        @keyframes error-message-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        @keyframes fade-in-up {
          from { 
            opacity: 0;
            transform: translateY(10px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .error-pulse {
          animation: error-pulse 2s infinite;
        }
        
        .error-shake {
          animation: error-shake 2s infinite;
        }
        
        .quiz-icon-orbit-disrupted {
          position: relative;
          width: 120px;
          height: 120px;
          animation: orbit-disrupted 8s infinite ease-in-out;
        }
        
        .quiz-icon-orbit-disrupted > svg {
          position: absolute;
          transform-origin: center;
        }
        
        .quiz-icon-orbit-disrupted > svg:nth-child(1) {
          top: -24px;
          left: calc(50% - 16px);
        }
        
        .quiz-icon-orbit-disrupted > svg:nth-child(2) {
          top: calc(50% - 16px);
          right: -20px;
        }
        
        .quiz-icon-orbit-disrupted > svg:nth-child(3) {
          bottom: -24px;
          left: calc(50% - 16px);
        }
        
        .paper-scattered-1 {
          animation: scatter-1 4s infinite ease-in-out;
        }
        
        .paper-scattered-2 {
          animation: scatter-2 3.5s infinite ease-in-out;
        }
        
        .icon-flicker {
          animation: flicker 3s infinite ease-in-out;
        }
        
        .error-message {
          animation: error-message-blink 2s infinite;
        }

        .fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          animation-delay: 0.5s;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default ErrorLoadingExam;
